/**
 * Resolving page permissions - the pure half.
 *
 * Nothing here touches React, the network or `localStorage`, so every rule the
 * gate depends on is directly testable (`pageAccess.test.js`).
 *
 * See `config/pageAccess.js` for the model. In short: a screen's identity is
 * the first path segment under `/home`, every backend shape resolves to one,
 * and access is then set membership.
 */

import {
  ALWAYS_OPEN_SEGMENTS,
  DEFAULT_ASSIGNABLE_SEGMENTS,
  KNOWN_ROUTE_SEGMENTS,
  NAME_TO_SEGMENT,
  SCREEN_SUBROUTES,
} from '../config/pageAccess';

/* ------------------------------------------------------------------ lookups */

/** sub-route segment -> owning segment */
const SUBROUTE_TO_OWNER = new Map();

Object.entries(SCREEN_SUBROUTES).forEach(([owner, subroutes]) => {
  SUBROUTE_TO_OWNER.set(owner.toLowerCase(), owner.toLowerCase());
  subroutes.forEach((segment) =>
    SUBROUTE_TO_OWNER.set(segment.toLowerCase(), owner.toLowerCase())
  );
});

/**
 * Fold the spelling differences the backend actually produces, so a grant is
 * never lost to a stray space or a hamza.
 *
 * The live master list prefixes several names with a literal `\r\n`
 * (`"\r\nالفعاليات"`), spells the same screen `إرشادات` in one place and
 * `الإرشادات` in another, and `userRoles` arrives as ONE comma-joined string,
 * so leading spaces are routine.
 *
 * Both sides of every comparison go through this, so folding cannot make two
 * different screens collide - they differ by far more than a diacritic.
 */
export const normaliseName = (value) =>
  String(value ?? '')
    .replace(/[ً-ْـ]/g, '') // harakat and tatweel
    .replace(/[آأإ]/g, 'ا') // آ أ إ -> ا
    .replace(/ى/g, 'ي') // ى -> ي
    .replace(/ة/g, 'ه') // ة -> ه
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** normalised Arabic name -> owning segment */
const NAME_TO_OWNER = new Map(
  Object.entries(NAME_TO_SEGMENT).map(([name, segment]) => [
    normaliseName(name),
    segment.toLowerCase(),
  ])
);

const ALWAYS_OPEN = new Set(ALWAYS_OPEN_SEGMENTS.map((s) => s.toLowerCase()));

/* ----------------------------------------------------------------- resolving */

/**
 * The first route segment under `/home`, lower-cased.
 *
 * Accepts every shape the app produces: `/home/news`, `home/news`, `news`,
 * `/home/events/event`, `/home/edit-news/42`, and a trailing slash or query.
 * Returns `''` for `/home` itself and for an empty path (a group heading).
 */
export const segmentOfPath = (path) => {
  const clean = String(path ?? '')
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '');

  if (!clean) return '';

  const parts = clean.split('/').filter(Boolean);
  if (parts[0]?.toLowerCase() === 'home') parts.shift();

  return (parts[0] ?? '').toLowerCase();
};

/**
 * Which screen owns this route?
 *
 * A sub-route resolves to its owner (`add-news` -> `news`); anything else owns
 * itself, so a page the backend adds tomorrow is gated under its own segment
 * without this file being touched.
 *
 * `null` only for `/home` itself and for an empty path.
 */
export const ownerOfPath = (path) => {
  const segment = segmentOfPath(path);
  if (!segment) return null;
  return SUBROUTE_TO_OWNER.get(segment) ?? segment;
};

/** Which screen does this Arabic label name? `null` when unrecognised. */
export const ownerOfName = (name) => {
  const key = normaliseName(name);
  if (!key) return null;
  return NAME_TO_OWNER.get(key) ?? null;
};

/**
 * Resolve any backend row - master list or sidebar tree - to an owner.
 *
 * ROUTE FIRST, and the two endpoints disagree on what to call it: the master
 * list (`GetRolePages`) sends `route`, the sidebar tree (`cmsPages`) sends
 * `path`. Both are read. The name is only a fallback, because it is the field
 * that gets re-worded.
 */
export const ownerOfRow = (row) => {
  if (!row) return null;

  const fromPath = ownerOfPath(row.path ?? row.route);
  if (fromPath) return fromPath;

  return ownerOfName(row.name ?? row.pageName ?? row.title);
};

/* ------------------------------------------------------------------- grants */

/**
 * Depth-first walk of a menu tree, parents before children. Also accepts the
 * flat master list, which simply has no `nested`.
 */
export const flattenPages = (pages) => {
  const out = [];
  const walk = (rows) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      if (!row) return;
      out.push(row);
      walk(row.nested);
    });
  };
  walk(pages);
  return out;
};

/**
 * The owners a user holds, from their own menu tree.
 *
 * Group rows (`طلبات`, `المركز الإعلامي`, `المجلات والأبحاث`) carry an empty
 * route and resolve to nothing. That is correct: a group is a heading, not a
 * screen, and it contributes only its children.
 */
export const ownersOf = (pages) => {
  const owners = new Set();
  flattenPages(pages).forEach((row) => {
    const owner = ownerOfRow(row);
    if (owner) owners.add(owner);
  });
  return owners;
};

/**
 * What may be enforced: the live master list, or the dated snapshot when it
 * cannot be read.
 *
 * `GetRolePages` is plausibly administrator-only — it was called from exactly
 * one screen, Users — and an empty assignable set means "enforce nothing",
 * which would silently disable the gate for the restricted operators it exists
 * for. The snapshot in `config/pageAccess.js` closes that hole without
 * risking a lock-out; see the note there.
 *
 * @param {Array} rolePages the `GetRolePages` rows, or an empty array
 * @returns {{owners: Set<string>, fromApi: boolean}}
 */
export const assignableOwners = (rolePages) => {
  const fromApi = ownersOf(rolePages);
  if (fromApi.size > 0) return { owners: fromApi, fromApi: true };

  return { owners: new Set(DEFAULT_ASSIGNABLE_SEGMENTS), fromApi: false };
};

/**
 * May this screen be enforced at all?
 *
 * ONLY IF THE BACKEND OFFERS IT. A screen that is not in the master list cannot
 * be granted to anyone, so gating it would hide a working screen from every
 * operator - administrators included - with no way to grant it back. Those
 * routes stay open, exactly as they are today.
 *
 * This is self-correcting in both directions and needs no maintenance: the day
 * a screen appears in `GetRolePages` it becomes assignable AND enforced; if one
 * is withdrawn, it reverts to open rather than becoming unreachable.
 *
 * While the master list has not loaded (or the call failed) `assignable` is
 * empty, and the honest answer is "nothing is enforceable yet" - the provider
 * holds the gate closed in that window rather than guessing. See
 * `Context/PageAccessContext.jsx`.
 *
 * @param {string} owner
 * @param {Set<string>} assignable owners present in `GetRolePages`
 */
export const isGateable = (owner, assignable) =>
  Boolean(owner) && Boolean(assignable?.has(owner));

/* -------------------------------------------------------------------- access */

/**
 * The access decision for one route.
 *
 * @param {string} pathname        e.g. `/home/edit-news/42`
 * @param {Set<string>} granted    owners the user holds
 * @param {Set<string>} assignable owners `GetRolePages` offers
 */
export const canAccessPath = (pathname, granted, assignable) => {
  const segment = segmentOfPath(pathname);
  if (ALWAYS_OPEN.has(segment)) return true;

  const owner = ownerOfPath(pathname);
  if (!owner) return true;

  if (!isGateable(owner, assignable)) return true;

  return Boolean(granted?.has(owner));
};

/* ------------------------------------------------------------------ landing */

/** The screen shown to a user who holds no pages at all. */
export const NO_PERMISSION_PATH = '/home/no-permission';

/**
 * Where to send someone the moment they sign in, and what `/home` resolves to.
 *
 * The first row of `cmsPages` is NOT reliably a screen. Three rows in the live
 * list are GROUP HEADINGS with an empty route, and the old code assigned
 * `cmsPages[0].path` straight to `window.location.href` - so a user whose first
 * row was a group was sent to `undefined`, and `/home` redirected to itself
 * forever.
 *
 * This walks the whole tree in menu order and returns the first row that is a
 * real, reachable screen.
 *
 * @returns {string|null} an absolute `/home/...` path, or `null` when the user
 *          holds no usable page - the caller sends them to NO_PERMISSION_PATH.
 */
export const firstGrantedPath = (pages) => {
  const rows = flattenPages(pages);

  for (const row of rows) {
    const raw = String(row?.path ?? row?.route ?? '').trim();
    if (!raw) continue; // a group heading

    // OPEN-REDIRECT GUARD. This value reaches `window.location.href`. It is
    // server-controlled and so trusted today, but `//evil.example` is a
    // protocol-relative URL a browser treats as absolute, and a scheme is worse
    // still. Both cost one line to refuse.
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) continue;

    const absolute = raw.startsWith('/') ? raw : `/home/${raw}`;
    if (segmentOfPath(absolute) === '') continue; // `/home` itself

    return absolute;
  }

  return null;
};

/** `firstGrantedPath`, with the no-permission screen as the fallback. */
export const landingPath = (pages) =>
  firstGrantedPath(pages) ?? NO_PERMISSION_PATH;

/* ----------------------------------------------------------- diagnostics */

/**
 * Name the screens that exist as routes but cannot be granted, so the gap is
 * visible while it is being closed rather than rediscovered months later.
 *
 * Development only - it is noise in a production console, and the list is a
 * map of what is NOT protected.
 */
export const logUngatedScreens = (assignable) => {
  if (process.env.NODE_ENV === 'production') return;
  if (!assignable || assignable.size === 0) return;

  const ungated = KNOWN_ROUTE_SEGMENTS.filter(
    (segment) => !assignable.has(segment)
  );

  if (ungated.length === 0) return;

  // eslint-disable-next-line no-console
  console.info(
    '[pageAccess] not assignable in GetRolePages, so left open to every ' +
      `signed-in operator: ${ungated.join(', ')}`
  );
};
